'use client';

import React, {
  PropsWithChildren,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import clsx, { ClassValue } from 'clsx';

import { searchFieldDropdownPanelClass } from '@/components/search/fields/search-field-dropdown-menu';
import Button from '@/components/ui/button';

const VIEWPORT_MARGIN = 16;
const DEFAULT_PANEL_TOP_OFFSET = 4;

type PanelPosition = {
  top: number;
  left: number;
  minWidth?: number;
  maxWidth?: number;
};

type SearchFieldPopoverProps = PropsWithChildren<{
  id: string;
  trigger: React.ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApply?: () => void;
  onClear?: () => void;
  showFooter?: boolean;
  /** When true, panel min-width matches the trigger width (e.g. dropdown menus). */
  minWidthFromTrigger?: boolean;
  /** Gap between the trigger bottom edge and the panel top (px). */
  panelTopOffset?: number;
  className?: string;
  classNames?: Partial<{
    panel: ClassValue;
    footer: ClassValue;
  }>;
}>;

const getPanelPosition = (
  triggerEl: HTMLElement,
  minWidthFromTrigger: boolean,
  panelTopOffset: number
): PanelPosition => {
  const triggerRect = triggerEl.getBoundingClientRect();
  const shell = minWidthFromTrigger
    ? triggerEl.closest('[data-search-fields-shell]')
    : null;
  const isStackedShell = shell instanceof HTMLElement;
  const alignRect = isStackedShell
    ? shell.getBoundingClientRect()
    : triggerRect;

  if (minWidthFromTrigger) {
    let left = isStackedShell ? alignRect.left : triggerRect.left;
    let minWidth = isStackedShell ? alignRect.width : triggerRect.width;
    let maxWidth = window.innerWidth - left - VIEWPORT_MARGIN;

    if (left < VIEWPORT_MARGIN) {
      const inset = VIEWPORT_MARGIN - left;
      left = VIEWPORT_MARGIN;
      minWidth = Math.max(0, minWidth - inset);
      maxWidth = window.innerWidth - 2 * VIEWPORT_MARGIN;
    }

    if (minWidth > maxWidth) {
      minWidth = maxWidth;
    }

    return {
      top: triggerRect.bottom + panelTopOffset,
      left,
      minWidth,
      maxWidth,
    };
  }

  return {
    top: triggerRect.bottom + panelTopOffset,
    left: Math.max(
      VIEWPORT_MARGIN,
      Math.min(
        triggerRect.left,
        window.innerWidth -
          Math.min(window.innerWidth - 32, 384) -
          VIEWPORT_MARGIN
      )
    ),
    maxWidth: window.innerWidth - 2 * VIEWPORT_MARGIN,
  };
};

export const SearchFieldPopover = ({
  id,
  trigger,
  open,
  onOpenChange,
  onApply,
  onClear,
  showFooter = Boolean(onClear && onApply),
  minWidthFromTrigger = false,
  panelTopOffset = DEFAULT_PANEL_TOP_OFFSET,
  className,
  classNames,
  children,
}: SearchFieldPopoverProps) => {
  const [panelPosition, setPanelPosition] = useState<PanelPosition | null>(
    null
  );
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const openRef = useRef(open);
  openRef.current = open;

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const el = triggerRef.current;
    const update = () =>
      setPanelPosition(getPanelPosition(el, minWidthFromTrigger, panelTopOffset));
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, minWidthFromTrigger, panelTopOffset]);

  const closeAndApply = useCallback(() => {
    if (!openRef.current) return;
    openRef.current = false;
    onOpenChange(false);
    onApply?.();
  }, [onApply, onOpenChange]);

  useEffect(() => {
    if (!open) return;
    const handle = (e: Event) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setTimeout(closeAndApply, 0);
    };
    document.addEventListener('click', handle, true);
    return () => document.removeEventListener('click', handle, true);
  }, [open, closeAndApply]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAndApply();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, closeAndApply]);

  const overlayPortal =
    typeof document !== 'undefined' &&
    open &&
    createPortal(
      <>
        <div
          role="presentation"
          data-testid="search-field-popover-backdrop"
          className="fixed inset-0 z-[9998] h-full w-dvw bg-transparent"
          onClick={() => closeAndApply()}
        />
        {panelPosition && (
          <div
            ref={panelRef}
            id={id}
            className={clsx(
              'fixed z-[9999] flex w-max max-w-[calc(100dvw-2rem)] flex-col overflow-y-auto bg-white',
              searchFieldDropdownPanelClass,
              classNames?.panel
            )}
            style={{
              top: panelPosition.top,
              left: panelPosition.left,
              ...(panelPosition.minWidth != null
                ? { minWidth: panelPosition.minWidth }
                : {}),
              ...(panelPosition.maxWidth != null
                ? { maxWidth: panelPosition.maxWidth }
                : {}),
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-full overflow-y-auto">{children}</div>
            {showFooter && (
              <div
                className={clsx(
                  'flex justify-between gap-4 border-t border-gray-2 bg-gray-1 p-4',
                  classNames?.footer
                )}
              >
                <Button
                  style="text"
                  size="small"
                  type="button"
                  className={clsx(
                    !onClear && 'invisible',
                    'w-fit text-gray-7 hover:!text-gray-9'
                  )}
                  onClick={() => onClear?.()}
                >
                  Clear
                </Button>
                <Button
                  size="small"
                  type="button"
                  style="secondary"
                  onClick={() => closeAndApply()}
                >
                  Apply
                </Button>
              </div>
            )}
          </div>
        )}
      </>,
      document.body
    );

  return (
    <div className={clsx('relative h-full min-w-0', className)}>
      <div
        ref={triggerRef}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-haspopup="true"
        className="h-full w-full cursor-pointer"
        onClick={() => onOpenChange(!open)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpenChange(!open);
          }
        }}
      >
        {trigger}
      </div>
      {overlayPortal}
    </div>
  );
};
